import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';

const AlertContext = createContext();

const initialState = {
  alerts: [],
  activeAlerts: [],
  alertHistory: [],
  unreadCount: 0,
  settings: {
    enableSound: true,
    enablePush: true,
    severity: 'medium', // minimum severity to show
    autoClose: true,
    autoCloseDelay: 5000
  }
};

const alertReducer = (state, action) => {
  switch (action.type) {
    case 'ADD_ALERT':
      const newAlert = {
        id: Date.now(),
        timestamp: new Date(),
        read: false,
        ...action.payload
      };
      return {
        ...state,
        alerts: [newAlert, ...state.alerts],
        activeAlerts: [newAlert, ...state.activeAlerts],
        unreadCount: state.unreadCount + 1
      };

    case 'DISMISS_ALERT':
      return {
        ...state,
        activeAlerts: state.activeAlerts.filter(alert => alert.id !== action.payload)
      };

    case 'MARK_AS_READ':
      return {
        ...state,
        alerts: state.alerts.map(alert =>
          alert.id === action.payload ? { ...alert, read: true } : alert
        ),
        unreadCount: Math.max(0, state.unreadCount - 1)
      };

    case 'MARK_ALL_AS_READ':
      return {
        ...state,
        alerts: state.alerts.map(alert => ({ ...alert, read: true })),
        unreadCount: 0
      };

    case 'CLEAR_ACTIVE_ALERTS':
      return {
        ...state,
        activeAlerts: []
      };

    case 'CLEAR_ALL_ALERTS':
      return {
        ...state,
        alerts: [],
        activeAlerts: [],
        alertHistory: [...state.alertHistory, ...state.alerts],
        unreadCount: 0
      };

    case 'UPDATE_SETTINGS':
      return {
        ...state,
        settings: { ...state.settings, ...action.payload }
      };

    default:
      return state;
  }
};

export const AlertProvider = ({ children }) => {
  const [state, dispatch] = useReducer(alertReducer, initialState);

  const addAlert = useCallback((alertData) => {
    const severityLevels = { low: 1, medium: 2, high: 3, critical: 4 };
    const minSeverity = severityLevels[state.settings.severity];
    const alertSeverity = severityLevels[alertData.severity];

    if (alertSeverity >= minSeverity) {
      dispatch({ type: 'ADD_ALERT', payload: alertData });

      if (state.settings.enableSound && alertSeverity >= 2) {
        console.log('🔊 Alert sound played');
      }

      if (state.settings.enablePush && 'Notification' in window) {
        if (Notification.permission === 'granted') {
          new Notification(alertData.title, {
            body: alertData.message,
            icon: '/favicon.ico'
          });
        }
      }
    }
  }, [state.settings.severity, state.settings.enableSound, state.settings.enablePush]);

  // Auto-dismiss alerts based on settings
  useEffect(() => {
    if (!state.settings.autoClose) return;

    const timeouts = state.activeAlerts.map(alert => {
      if (alert.severity === 'critical') return null; // Don't auto-close critical alerts
      
      return setTimeout(() => {
        dispatch({ type: 'DISMISS_ALERT', payload: alert.id });
      }, state.settings.autoCloseDelay);
    });

    return () => {
      timeouts.forEach(timeout => timeout && clearTimeout(timeout));
    };
  }, [state.activeAlerts, state.settings.autoClose, state.settings.autoCloseDelay]);

  // Simulate system alerts
  useEffect(() => {
    const generateSystemAlert = () => {
      const alertTypes = [
        {
          type: 'system',
          severity: 'low',
          title: 'System Update',
          message: 'System maintenance completed successfully',
          category: 'system'
        },
        {
          type: 'inventory',
          severity: 'medium',
          title: 'Low Stock Alert',
          message: 'GOLD-001 inventory is running low',
          category: 'inventory'
        },
        {
          type: 'security',
          severity: 'high',
          title: 'Security Alert',
          message: 'Unusual access pattern detected',
          category: 'security'
        },
        {
          type: 'blockchain',
          severity: 'medium',
          title: 'Network Congestion',
          message: 'Blockchain network experiencing high traffic',
          category: 'blockchain'
        },
        {
          type: 'quality',
          severity: 'high',
          title: 'Quality Issue',
          message: 'Quality verification failed for SILVER-002',
          category: 'quality'
        }
      ];

      if (Math.random() < 0.1) { // 10% chance every interval
        const randomAlert = alertTypes[Math.floor(Math.random() * alertTypes.length)];
        addAlert(randomAlert);
      }
    };

    const interval = setInterval(generateSystemAlert, 10000); // Check every 10 seconds
    return () => clearInterval(interval);
  }, [addAlert]);

  const dismissAlert = (alertId) => {
    dispatch({ type: 'DISMISS_ALERT', payload: alertId });
  };

  const markAsRead = (alertId) => {
    dispatch({ type: 'MARK_AS_READ', payload: alertId });
  };

  const markAllAsRead = () => {
    dispatch({ type: 'MARK_ALL_AS_READ' });
  };

  const clearActiveAlerts = () => {
    dispatch({ type: 'CLEAR_ACTIVE_ALERTS' });
  };

  const clearAllAlerts = () => {
    dispatch({ type: 'CLEAR_ALL_ALERTS' });
  };

  const updateSettings = (newSettings) => {
    dispatch({ type: 'UPDATE_SETTINGS', payload: newSettings });
  };

  const requestNotificationPermission = async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }
    return false;
  };

  const getAlertsByCategory = (category) => {
    return state.alerts.filter(alert => alert.category === category);
  };

  const getAlertsBySeverity = (severity) => {
    return state.alerts.filter(alert => alert.severity === severity);
  };

  const value = {
    ...state,
    addAlert,
    dismissAlert,
    markAsRead,
    markAllAsRead,
    clearActiveAlerts,
    clearAllAlerts,
    updateSettings,
    requestNotificationPermission,
    getAlertsByCategory,
    getAlertsBySeverity
  };

  return (
    <AlertContext.Provider value={value}>
      {children}
    </AlertContext.Provider>
  );
};

export const useAlert = () => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlert must be used within an AlertProvider');
  }
  return context;
};

export default AlertContext; 